// ═══════════════════════════════════════════════════════════════
// API DE REPORTES DE NÓMINA
// Archivo: src/api/payrollReports.js
// ═══════════════════════════════════════════════════════════════

import api from './config';

// ─────────────────────────────────────────────────────────────
// Helper: descargar blob como archivo
// ─────────────────────────────────────────────────────────────
export function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a   = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────
// Helper: construir query string de filtros
// ─────────────────────────────────────────────────────────────
function buildParams(filters = {}) {
  const p = {};
  if (filters.companyId)    p.companyId    = filters.companyId;
  if (filters.mes)          p.mes          = filters.mes;
  if (filters.periodoDesde) p.periodoDesde = filters.periodoDesde;
  if (filters.periodoHasta) p.periodoHasta = filters.periodoHasta;
  if (filters.payrollId)    p.payrollId    = filters.payrollId;
  if (filters.tipo)         p.tipo         = filters.tipo;
  if (filters.departmentId) p.departmentId = filters.departmentId;
  if (filters.orgUnitId)    p.orgUnitId    = filters.orgUnitId;
  if (filters.employeeId)   p.employeeId   = filters.employeeId;
  if (filters.page)         p.page         = filters.page;
  if (filters.pageSize)     p.pageSize     = filters.pageSize;
  return p;
}

// ─────────────────────────────────────────────────────────────
// Obtener nóminas cerradas disponibles para reportes
// ─────────────────────────────────────────────────────────────
export const getAvailablePayrolls = async (companyId, periodoDesde, periodoHasta) => {
  const response = await api.get('/payroll-reports/available-payrolls', {
    params: { companyId, periodoDesde, periodoHasta },
  });
  return response.data;
};

// ─────────────────────────────────────────────────────────────
// Obtener datos de un reporte (JSON, paginado)
// reportType: 'period-summary' | 'employee-detail' | 'by-department' | ...
// ─────────────────────────────────────────────────────────────
export const getReportData = async (reportType, filters = {}) => {
  const response = await api.get(`/payroll-reports/${reportType}`, {
    params: buildParams(filters),
  });
  return response.data;
};

// ─────────────────────────────────────────────────────────────
// Exportar a Excel y descargar
// ─────────────────────────────────────────────────────────────
export const exportReportExcel = async (reportType, filters = {}) => {
  const response = await api.get(`/payroll-reports/${reportType}/export/excel`, {
    params: buildParams(filters),
    responseType: 'blob',
  });
  const periodo = filters.periodoDesde
    ? `_${filters.periodoDesde}${filters.periodoHasta ? '_' + filters.periodoHasta : ''}`
    : '';
  downloadBlob(response.data, `reporte_${reportType}${periodo}.xlsx`);
};

// ─────────────────────────────────────────────────────────────
// Exportar a PDF y descargar
// ─────────────────────────────────────────────────────────────
export const exportReportPDF = async (reportType, filters = {}) => {
  const response = await api.get(`/payroll-reports/${reportType}/export/pdf`, {
    params: buildParams(filters),
    responseType: 'blob',
  });
  const periodo = filters.periodoDesde
    ? `_${filters.periodoDesde}${filters.periodoHasta ? '_' + filters.periodoHasta : ''}`
    : '';
  downloadBlob(response.data, `reporte_${reportType}${periodo}.pdf`);
};

// ─────────────────────────────────────────────────────────────
// Obtener empleados de una nómina (para recibos)
// ─────────────────────────────────────────────────────────────
export const getPayrollEmployees = async (payrollId) => {
  const response = await api.get(`/payroll-reports/payroll-employees/${payrollId}`);
  return response.data;
};

// ─────────────────────────────────────────────────────────────
// Descargar recibo individual (PDF)
// ─────────────────────────────────────────────────────────────
export const downloadPayslip = async (payrollId, employeeId, filename) => {
  const response = await api.get(`/payroll-reports/payslip/${payrollId}/${employeeId}`, {
    responseType: 'blob',
  });
  downloadBlob(response.data, filename || `recibo_${employeeId}.pdf`);
};

// ─────────────────────────────────────────────────────────────
// Descargar recibos masivos (ZIP)
// ─────────────────────────────────────────────────────────────
export const downloadBulkPayslips = async (payrollId, filename) => {
  const response = await api.post(`/payroll-reports/payslips/bulk/${payrollId}`, {}, {
    responseType: 'blob',
  });
  downloadBlob(response.data, filename || `recibos_${payrollId}.zip`);
};

// ─────────────────────────────────────────────────────────────
// BONIFICACIONES — recibos en el módulo reportes
// ─────────────────────────────────────────────────────────────
export const getBonificacionesPagadas = async (companyId) => {
  const response = await api.get('/bonificaciones', { params: { companyId } });
  const all = response.data?.bonificaciones || response.data || [];
  return all.filter(b => b.estado === 'pagado');
};

export const getBonificacionDetalle = async (id) => {
  const response = await api.get(`/bonificaciones/${id}/detalle`);
  const rows = response.data?.detalle || response.data || [];
  return rows.filter(d => d.estado !== 'excluido');
};

export const downloadBonificacionPayslip = async (bonId, detalleId, filename) => {
  const response = await api.get(`/bonificaciones/${bonId}/detalle/${detalleId}/payslip`, {
    responseType: 'blob',
  });
  downloadBlob(response.data, filename || `bonificacion_${detalleId}.pdf`);
};

export const downloadBonificacionBulk = async (bonId, filename) => {
  const response = await api.post(`/bonificaciones/${bonId}/payslips/bulk`, {}, {
    responseType: 'blob',
  });
  downloadBlob(response.data, filename || `bonificaciones_${bonId}.zip`);
};

// ─────────────────────────────────────────────────────────────
// REGALÍA PASCUAL — recibos en el módulo reportes
// ─────────────────────────────────────────────────────────────
export const getRegaliasPageadas = async (companyId) => {
  const response = await api.get('/regalia-pascual', { params: { companyId } });
  const all = response.data?.procesos || response.data || [];
  return all.filter(r => r.estado === 'pagado');
};

export const getRegaliaRecords = async (id) => {
  const response = await api.get(`/regalia-pascual/${id}/records`, {
    params: { pageSize: 1000 },
  });
  return response.data?.records || response.data || [];
};

export const downloadRegaliaPayslip = async (regaliaId, recordId, filename) => {
  const response = await api.get(`/regalia-pascual/${regaliaId}/records/${recordId}/payslip`, {
    responseType: 'blob',
  });
  downloadBlob(response.data, filename || `regalia_${recordId}.pdf`);
};

export const downloadRegaliaBulk = async (regaliaId, filename) => {
  const response = await api.post(`/regalia-pascual/${regaliaId}/payslips/bulk`, {}, {
    responseType: 'blob',
  });
  downloadBlob(response.data, filename || `regalia_${regaliaId}.zip`);
};

// ─────────────────────────────────────────────────────────────
// Exportar a CSV y descargar
// ─────────────────────────────────────────────────────────────
export const exportReportCSV = async (reportType, filters = {}) => {
  const response = await api.get(`/payroll-reports/${reportType}/export/csv`, {
    params: buildParams(filters),
    responseType: 'blob',
  });
  const periodo = filters.periodoDesde
    ? `_${filters.periodoDesde}${filters.periodoHasta ? '_' + filters.periodoHasta : ''}`
    : '';
  downloadBlob(response.data, `reporte_${reportType}${periodo}.csv`);
};
